/* filepath: packages/volto-wildcard-theme/src/components/Navigation/NavigationDropdown.jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { flattenToAppURL } from '@plone/volto/helpers';
import { getNavigation } from '@plone/volto/actions';

const normalize = (url) => flattenToAppURL(url || '');
const stripTrailing = (s) => (s || '').replace(/\/+$/, '') || '/';

function isActivePath(pathname, itemUrl) {
  const p = stripTrailing(pathname);
  const u = stripTrailing(normalize(itemUrl));
  if (u === '/') return p === '/';
  return p === u || p.startsWith(`${u}/`);
}

function getItemUrl(item) {
  return item?.['@id'] || item?.url || '';
}

function childrenOf(item) {
  return Array.isArray(item?.items) ? item.items : [];
}

function hasChildren(item) {
  return childrenOf(item).length > 0;
}

function useIsMobile(breakpointPx = 991) {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);

    const update = () => setIsMobile(mq.matches);
    update();

    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, [breakpointPx]);

  return isMobile;
}

export default function NavigationDropdown({ pathname, root = '/', depth = 4 }) {
  const isMobile = useIsMobile(991);

  const dispatch = useDispatch();
  const items = useSelector((state) => state?.navigation?.items) || [];
  const loaded = useSelector((state) => state?.navigation?.loaded);

  // desktop dropdown open
  const [openKey, setOpenKey] = React.useState(null);

  // mobile drawer open + per-parent expand state
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileExpanded, setMobileExpanded] = React.useState({}); // { [key]: true/false }

  // Close menus on route change
  React.useEffect(() => {
    setOpenKey(null);
    setMobileOpen(false);
  }, [pathname]);

  // Body scroll lock hook (your CSS uses this class)
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('has-mobile-nav-open', mobileOpen);
    return () => document.body.classList.remove('has-mobile-nav-open');
  }, [mobileOpen]);

  // Fetch nav; refetch if shallow (top-level exists but children missing)
  React.useEffect(() => {
    const shallow =
      depth > 1 && items.length > 0 && items.every((it) => childrenOf(it).length === 0);

    const shouldFetch = !loaded || items.length === 0 || shallow;
    if (shouldFetch) dispatch(getNavigation(root, depth));
  }, [dispatch, root, depth, loaded, items]);

  // Close desktop dropdown on outside click
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target?.closest?.('.nav-dd')) setOpenKey(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const mkIsActive =
    (url) =>
    (_match, location) =>
      isActivePath(location?.pathname || pathname, url);

  const toggleDesktop = (key) => setOpenKey((v) => (v === key ? null : key));

  const toggleMobileDrawer = () => setMobileOpen((v) => !v);

  const toggleMobileExpand = (key) =>
    setMobileExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // -----------------------
  // Mobile drawer rendering
  // -----------------------
  if (isMobile) {
    return (
      <nav className="navigation nav-dd" id="navigation" aria-label="Site">
        <div className="hamburger-wrapper mobile tablet only">
          <button
            type="button"
            className={`hamburger${mobileOpen ? ' is-active' : ''}`}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen ? 'true' : 'false'}
            onClick={toggleMobileDrawer}
          >
            <span className="hamburger-box">
              <span className="hamburger-inner" />
            </span>
          </button>
        </div>

        {mobileOpen ? (
          <div className="mobile-menu" role="dialog" aria-label="Mobile navigation">
            <div className="mobile-menu-nav">
              <div className="ui menu">
                {items.map((item) => {
                  const url = getItemUrl(item);
                  const key = url || item?.title;
                  const kids = childrenOf(item);
                  const expanded = !!mobileExpanded[key];
                  const active = isActivePath(pathname, url);

                  return (
                    <div key={key} className={`item${active ? ' active' : ''}`}>
                      <div className="nav-dd-mobileRow">
                        <NavLink
                          className="nav-dd-mobileLink"
                          activeClassName="active"
                          to={normalize(url)}
                          isActive={mkIsActive(url)}
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.title}
                        </NavLink>

                        {hasChildren(item) ? (
                          <button
                            type="button"
                            className="nav-dd-mobileToggle"
                            aria-label={expanded ? `Collapse ${item.title}` : `Expand ${item.title}`}
                            aria-expanded={expanded ? 'true' : 'false'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleMobileExpand(key);
                            }}
                          >
                            <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
                          </button>
                        ) : null}
                      </div>

                      {/* Always render: allows max-height/opacity transition */}
                        {hasChildren(item) ? (
                        <div className={`nav-dd-mobileSubmenu${expanded ? ' is-open' : ''}`}>
                            {kids.map((child) => {
                            const childUrl = getItemUrl(child);
                            const childKey = childUrl || child?.title;
                            return (
                                <NavLink
                                key={childKey}
                                className="item nav-dd-mobileSubitem"
                                activeClassName="active"
                                to={normalize(childUrl)}
                                isActive={mkIsActive(childUrl)}
                                onClick={() => setMobileOpen(false)}
                                >
                                {child.title}
                                </NavLink>
                            );
                            })}
                        </div>
                        ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </nav>
    );
  }

  // -----------------------
  // Desktop dropdown rendering
  // -----------------------
  return (
    <nav className="navigation nav-dd" id="navigation" aria-label="Site">
      <div className="ui pointing secondary stackable computer large screen widescreen only menu">
        {items.map((item) => {
          const url = getItemUrl(item);
          const key = url || item?.title;
          const active = isActivePath(pathname, url);
          const kids = childrenOf(item);
          const isOpen = openKey === key;

          if (!hasChildren(item)) {
            return (
              <NavLink
                key={key}
                className="item"
                activeClassName="active"
                to={normalize(url)}
                isActive={mkIsActive(url)}
              >
                {item.title}
              </NavLink>
            );
          }

          return (
            <div
              key={key}
              className={`item nav-dd__item${active ? ' active' : ''}${isOpen ? ' is-open' : ''}`}
            >
              <NavLink
                className="nav-dd__link"
                activeClassName="active"
                to={normalize(url)}
                isActive={mkIsActive(url)}
                onClick={() => setOpenKey(null)}
              >
                {item.title}
              </NavLink>

              <button
                type="button"
                className="nav-dd__caret"
                aria-label={isOpen ? `Collapse ${item.title}` : `Expand ${item.title}`}
                aria-expanded={isOpen ? 'true' : 'false'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleDesktop(key);
                }}
              >
                <span aria-hidden="true">▾</span>
              </button>

              <div className="nav-dd__menu" role="menu" aria-hidden={!isOpen}>
                {kids.map((child) => {
                  const childUrl = getItemUrl(child);
                  const childKey = childUrl || child?.title;
                  return (
                    <NavLink
                      key={childKey}
                      className="item"
                      activeClassName="active"
                      to={normalize(childUrl)}
                      isActive={mkIsActive(childUrl)}
                      onClick={() => setOpenKey(null)}
                    >
                      {child.title}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
}

NavigationDropdown.propTypes = {
  pathname: PropTypes.string.isRequired,
  root: PropTypes.string,
  depth: PropTypes.number,
};